/**
 * Bots Management Page
 * Create and manage AI bot players with advanced configuration
 */

import React, { useEffect, useState } from 'react';
import { adminService } from '../../services/adminService';

interface Bot {
  id: string;
  name: string;
  avatar?: string;
  skillLevel: number; // 1-10
  /** The server's BotDifficulty values (shown as Beginner / Intermediate / Advanced / Expert). */
  difficulty: 'EASY' | 'MEDIUM' | 'HARD' | 'EXPERT';
  isActive: boolean;
  tablesAssigned: number;
  gamesPlayed: number;
  gamesWon: number;
  totalChipsWon: number;
  aggressiveness: number; // 0-1
  bluffFrequency: number; // 0-1
  tightness: number; // 0-1
  playStyle: 'AGGRESSIVE' | 'PASSIVE' | 'BALANCED';
  foldProbability: number; // 0-1
  raiseProbability: number; // 0-1
  callProbability: number; // 0-1
  reactionTimeMs: number;
  startingChips: number;
  autoDeployToEmptyTables: boolean;
  createdAt: string;
}

interface BotStatistics {
  botId: string;
  botName: string;
  totalGames: number;
  wins: number;
  losses: number;
  winRate: number;
  avgChipsWon: number;
  totalChipsWon: number;
  favoriteHands: string[];
  mostCommonMoves: Array<{ move: string; count: number }>;
  biggestWin: number;
  biggestLoss: number;
  avgGameDuration: number;
}

interface Table {
  id: string;
  name: string;
  currentPlayers: number;
  maxPlayers: number;
}

const AVATAR_PRESETS = ['🤖', '🎮', '🎯', '🎲', '🃏', '👾', '🦾', '🧠', '⚡', '🔥'];

const BotsPage: React.FC = () => {
  const [bots, setBots] = useState<Bot[]>([]);
  const [tables, setTables] = useState<Table[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBots, setSelectedBots] = useState<Set<string>>(new Set());

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [currentBot, setCurrentBot] = useState<Bot | null>(null);
  const [botStats, setBotStats] = useState<BotStatistics | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Create bot form
  const [createForm, setCreateForm] = useState({
    name: '',
    avatar: '🤖',
    skillLevel: 5,
    difficulty: 'MEDIUM' as Bot['difficulty'],
    playStyle: 'BALANCED' as Bot['playStyle'],
    aggressiveness: 0.5,
    tightness: 0.5,
    bluffFrequency: 0.2,
    foldProbability: 0.3,
    raiseProbability: 0.4,
    callProbability: 0.3,
    reactionTimeMs: 2000,
    startingChips: 10000,
    autoDeployToEmptyTables: false,
  });

  // Settings form
  const [settingsForm, setSettingsForm] = useState<Partial<Bot>>({});

  // Deploy form
  const [deployTableId, setDeployTableId] = useState('');
  const [deployingBotId, setDeployingBotId] = useState<string | null>(null);

  useEffect(() => {
    loadBots();
    loadTables();
  }, []);

  const loadBots = async () => {
    try {
      setLoading(true);
      const data = await adminService.getBots({ page: 1, limit: 100 });
      setBots(data.data || []);
    } catch (error) {
      console.error('Failed to load bots:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTables = async () => {
    try {
      const data = await adminService.getTables({ page: 1, limit: 100 });
      setTables(data.data || []);
    } catch (error) {
      console.error('Failed to load tables:', error);
    }
  };

  const handleCreateBot = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await adminService.createBot(createForm);
      setShowCreateModal(false);
      resetCreateForm();
      loadBots();
    } catch (error) {
      console.error('Failed to create bot:', error);
      alert('Failed to create bot');
    }
  };

  const resetCreateForm = () => {
    setCreateForm({
      name: '',
      avatar: '🤖',
      skillLevel: 5,
      difficulty: 'MEDIUM',
      playStyle: 'BALANCED',
      aggressiveness: 0.5,
      tightness: 0.5,
      bluffFrequency: 0.2,
      foldProbability: 0.3,
      raiseProbability: 0.4,
      callProbability: 0.3,
      reactionTimeMs: 2000,
      startingChips: 10000,
      autoDeployToEmptyTables: false,
    });
  };

  const openSettings = (bot: Bot) => {
    setCurrentBot(bot);
    setSettingsForm({
      aggressiveness: bot.aggressiveness,
      tightness: bot.tightness,
      bluffFrequency: bot.bluffFrequency,
      foldProbability: bot.foldProbability,
      raiseProbability: bot.raiseProbability,
      callProbability: bot.callProbability,
      reactionTimeMs: bot.reactionTimeMs,
      playStyle: bot.playStyle,
    });
    setShowSettingsModal(true);
  };

  const handleUpdateSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentBot) return;

    try {
      await adminService.updateBot(currentBot.id, settingsForm);
      setShowSettingsModal(false);
      setCurrentBot(null);
      loadBots();
    } catch (error) {
      console.error('Failed to update bot settings:', error);
      alert('Failed to update bot settings');
    }
  };

  const viewStatistics = async (bot: Bot) => {
    try {
      setLoadingStats(true);
      setShowStatsModal(true);
      setCurrentBot(bot);

      // Mock statistics - replace with actual API call
      const stats: BotStatistics = {
        botId: bot.id,
        botName: bot.name,
        totalGames: bot.gamesPlayed,
        wins: bot.gamesWon,
        losses: bot.gamesPlayed - bot.gamesWon,
        winRate: bot.gamesPlayed > 0 ? (bot.gamesWon / bot.gamesPlayed) * 100 : 0,
        avgChipsWon: bot.gamesPlayed > 0 ? bot.totalChipsWon / bot.gamesPlayed : 0,
        totalChipsWon: bot.totalChipsWon,
        favoriteHands: ['A♠ A♥', 'K♠ K♦', 'Q♥ Q♣'],
        mostCommonMoves: [
          { move: 'FOLD', count: Math.floor(bot.gamesPlayed * 0.4) },
          { move: 'CALL', count: Math.floor(bot.gamesPlayed * 0.35) },
          { move: 'RAISE', count: Math.floor(bot.gamesPlayed * 0.2) },
          { move: 'ALL_IN', count: Math.floor(bot.gamesPlayed * 0.05) },
        ],
        biggestWin: Math.floor(Math.random() * 50000) + 10000,
        biggestLoss: Math.floor(Math.random() * 30000) + 5000,
        avgGameDuration: 420, // seconds
      };

      setBotStats(stats);
    } catch (error) {
      console.error('Failed to load bot statistics:', error);
      alert('Failed to load statistics');
    } finally {
      setLoadingStats(false);
    }
  };

  const openDeployModal = (botId: string) => {
    setDeployingBotId(botId);
    setDeployTableId('');
    setShowDeployModal(true);
  };

  const handleDeploy = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deployingBotId || !deployTableId) return;

    try {
      await adminService.deployBot(deployingBotId, { tableId: deployTableId });
      setShowDeployModal(false);
      setDeployingBotId(null);
      setDeployTableId('');
      loadBots();
      alert('Bot deployed successfully!');
    } catch (error) {
      console.error('Failed to deploy bot:', error);
      alert('Failed to deploy bot');
    }
  };

  const toggleBotStatus = async (botId: string, currentStatus: boolean) => {
    try {
      await adminService.setBotActive(botId, !currentStatus);
      loadBots();
    } catch (error) {
      console.error('Failed to toggle bot status:', error);
      alert(error instanceof Error ? error.message : 'Failed to update bot status');
    }
  };

  const deleteBot = async (botId: string, botName: string) => {
    if (!confirm(`Are you sure you want to delete bot "${botName}"?`)) return;

    try {
      await adminService.deleteBot(botId);
      loadBots();
    } catch (error) {
      console.error('Failed to delete bot:', error);
      alert('Failed to delete bot');
    }
  };

  const toggleBotSelection = (botId: string) => {
    const newSelection = new Set(selectedBots);
    if (newSelection.has(botId)) {
      newSelection.delete(botId);
    } else {
      newSelection.add(botId);
    }
    setSelectedBots(newSelection);
  };

  const selectAllBots = () => {
    if (selectedBots.size === bots.length) {
      setSelectedBots(new Set());
    } else {
      setSelectedBots(new Set(bots.map(b => b.id)));
    }
  };

  const bulkActivate = async () => {
    try {
      await Promise.all(
        Array.from(selectedBots).map(botId =>
          adminService.setBotActive(botId, true)
        )
      );
      loadBots();
      setSelectedBots(new Set());
    } catch (error) {
      console.error('Failed to bulk activate:', error);
      alert('Failed to activate bots');
    }
  };

  const bulkDeactivate = async () => {
    try {
      await Promise.all(
        Array.from(selectedBots).map(botId =>
          adminService.setBotActive(botId, false)
        )
      );
      loadBots();
      setSelectedBots(new Set());
    } catch (error) {
      console.error('Failed to bulk deactivate:', error);
      alert('Failed to deactivate bots');
    }
  };

  const getSkillColor = (level: number) => {
    if (level <= 3) return 'text-green-600';
    if (level <= 6) return 'text-yellow-600';
    if (level <= 8) return 'text-orange-600';
    return 'text-red-600';
  };

  const getDifficultyBadge = (difficulty: string) => {
    const badges = {
      EASY: 'bg-green-100 text-green-800',
      MEDIUM: 'bg-yellow-100 text-yellow-800',
      HARD: 'bg-orange-100 text-orange-800',
      EXPERT: 'bg-red-100 text-red-800',
    };
    return badges[difficulty as keyof typeof badges] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">🤖 Bots Management</h1>
            <p className="text-indigo-100 mt-1">Configure AI players with custom behaviors and strategies</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors font-bold flex items-center space-x-2"
          >
            <span>➕</span>
            <span>Create New Bot</span>
          </button>
        </div>
      </div>

      {/* Statistics Bar */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
          <div className="text-sm text-gray-600 font-medium">Total Bots</div>
          <div className="text-3xl font-bold text-gray-900 mt-1">{bots.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
          <div className="text-sm text-gray-600 font-medium">Active Bots</div>
          <div className="text-3xl font-bold text-green-600 mt-1">
            {bots.filter(b => b.isActive).length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
          <div className="text-sm text-gray-600 font-medium">Total Games</div>
          <div className="text-3xl font-bold text-purple-600 mt-1">
            {bots.reduce((sum, b) => sum + b.gamesPlayed, 0).toLocaleString()}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
          <div className="text-sm text-gray-600 font-medium">Deployed</div>
          <div className="text-3xl font-bold text-orange-600 mt-1">
            {bots.filter(b => b.tablesAssigned > 0).length}
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedBots.size > 0 && (
        <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-indigo-900">
              {selectedBots.size} bot{selectedBots.size > 1 ? 's' : ''} selected
            </div>
            <div className="flex space-x-3">
              <button
                onClick={bulkActivate}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                ✓ Activate All
              </button>
              <button
                onClick={bulkDeactivate}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                ⏸ Deactivate All
              </button>
              <button
                onClick={() => setSelectedBots(new Set())}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                ✕ Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bots Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            <div className="text-gray-500 mt-4">Loading bots...</div>
          </div>
        ) : bots.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <div className="text-6xl mb-4">🤖</div>
            <div className="text-xl font-medium">No bots created yet</div>
            <div className="text-sm mt-2">Create your first AI bot to get started!</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedBots.size === bots.length}
                      onChange={selectAllBots}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bot Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Skill Level
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tables
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Games Played
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Win Rate
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {bots.map((bot) => (
                  <tr key={bot.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedBots.has(bot.id)}
                        onChange={() => toggleBotSelection(bot.id)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="text-2xl">{bot.avatar || '🤖'}</div>
                        <div>
                          <div className="font-bold text-gray-900">{bot.name}</div>
                          <div className="text-xs text-gray-500">ID: {bot.id.slice(0, 8)}...</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <div className={`text-2xl font-bold ${getSkillColor(bot.skillLevel)}`}>
                          {bot.skillLevel}
                        </div>
                        <div className="text-xs text-gray-400">/10</div>
                      </div>
                      <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full mt-1 ${getDifficultyBadge(bot.difficulty)}`}>
                        {bot.difficulty}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleBotStatus(bot.id, bot.isActive)}
                        className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                          bot.isActive
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        }`}
                      >
                        {bot.isActive ? '● Active' : '○ Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-bold text-blue-600">{bot.tablesAssigned || 0}</span>
                      <span className="text-gray-500 text-sm"> tables</span>
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-900">
                      {bot.gamesPlayed.toLocaleString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <div className="font-bold text-green-600">
                          {bot.gamesPlayed > 0
                            ? ((bot.gamesWon / bot.gamesPlayed) * 100).toFixed(1)
                            : 0}%
                        </div>
                        <div className="text-xs text-gray-500">
                          ({bot.gamesWon}W)
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => openSettings(bot)}
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded transition-colors"
                          title="Edit Settings"
                        >
                          ⚙️ Settings
                        </button>
                        <button
                          onClick={() => openDeployModal(bot.id)}
                          className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded transition-colors"
                          title="Deploy to Table"
                        >
                          🚀 Deploy
                        </button>
                        <button
                          onClick={() => viewStatistics(bot)}
                          className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium rounded transition-colors"
                          title="View Statistics"
                        >
                          📊 Stats
                        </button>
                        <button
                          onClick={() => deleteBot(bot.id, bot.name)}
                          className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded transition-colors"
                          title="Delete Bot"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Bot Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 text-white">
              <h2 className="text-2xl font-bold">Create New Bot</h2>
              <p className="text-indigo-100 text-sm mt-1">Configure AI player behavior and strategy</p>
            </div>

            <form onSubmit={handleCreateBot} className="overflow-y-auto flex-1 p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Bot Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bot Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="e.g., Bot_JohnPoker"
                  />
                </div>

                {/* Avatar */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Avatar
                  </label>
                  <div className="grid grid-cols-5 gap-2">
                    {AVATAR_PRESETS.map(avatar => (
                      <button
                        key={avatar}
                        type="button"
                        onClick={() => setCreateForm({ ...createForm, avatar })}
                        className={`text-3xl p-2 rounded-lg border-2 transition-all ${
                          createForm.avatar === avatar
                            ? 'border-indigo-600 bg-indigo-50'
                            : 'border-gray-200 hover:border-indigo-300'
                        }`}
                      >
                        {avatar}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Skill Level */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Skill Level: <span className={`font-bold ${getSkillColor(createForm.skillLevel)}`}>{createForm.skillLevel}/10</span>
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={createForm.skillLevel}
                    onChange={(e) => setCreateForm({ ...createForm, skillLevel: parseInt(e.target.value) })}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Beginner</span>
                    <span>Expert</span>
                  </div>
                </div>

                {/* Difficulty */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Difficulty Preset
                  </label>
                  <select
                    value={createForm.difficulty}
                    onChange={(e) => setCreateForm({ ...createForm, difficulty: e.target.value as any })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="EASY">Beginner</option>
                    <option value="MEDIUM">Intermediate</option>
                    <option value="HARD">Advanced</option>
                    <option value="EXPERT">Expert</option>
                  </select>
                </div>

                {/* Play Style */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Play Style
                  </label>
                  <select
                    value={createForm.playStyle}
                    onChange={(e) => setCreateForm({ ...createForm, playStyle: e.target.value as any })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="AGGRESSIVE">Aggressive (More Raises)</option>
                    <option value="PASSIVE">Passive (More Calls/Folds)</option>
                    <option value="BALANCED">Balanced (Mixed Strategy)</option>
                  </select>
                </div>

                {/* Starting Chips */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Starting Chips
                  </label>
                  <input
                    type="number"
                    min="1000"
                    step="1000"
                    value={createForm.startingChips}
                    onChange={(e) => setCreateForm({ ...createForm, startingChips: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Advanced Settings */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Behavior Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Aggressiveness */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Aggressiveness: {Math.round(createForm.aggressiveness * 100)}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={createForm.aggressiveness}
                      onChange={(e) => setCreateForm({ ...createForm, aggressiveness: parseFloat(e.target.value) })}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-500"
                    />
                  </div>

                  {/* Tightness */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tightness: {Math.round(createForm.tightness * 100)}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={createForm.tightness}
                      onChange={(e) => setCreateForm({ ...createForm, tightness: parseFloat(e.target.value) })}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                    />
                  </div>

                  {/* Bluff Frequency */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bluff Frequency: {Math.round(createForm.bluffFrequency * 100)}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={createForm.bluffFrequency}
                      onChange={(e) => setCreateForm({ ...createForm, bluffFrequency: parseFloat(e.target.value) })}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-500"
                    />
                  </div>

                  {/* Reaction Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reaction Time: {createForm.reactionTimeMs}ms
                    </label>
                    <input
                      type="range"
                      min="500"
                      max="5000"
                      step="100"
                      value={createForm.reactionTimeMs}
                      onChange={(e) => setCreateForm({ ...createForm, reactionTimeMs: parseInt(e.target.value) })}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500"
                    />
                  </div>
                </div>
              </div>

              {/* Auto-deploy checkbox */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={createForm.autoDeployToEmptyTables}
                    onChange={(e) => setCreateForm({ ...createForm, autoDeployToEmptyTables: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div>
                    <div className="font-medium text-gray-900">Auto-deploy to empty tables</div>
                    <div className="text-sm text-gray-500">Automatically join tables that need players</div>
                  </div>
                </label>
              </div>

              <div className="flex space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); resetCreateForm(); }}
                  className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors"
                >
                  Create Bot
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && currentBot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 text-white">
              <h2 className="text-2xl font-bold">Bot Settings</h2>
              <p className="text-blue-100 text-sm mt-1">{currentBot.avatar} {currentBot.name}</p>
            </div>

            <form onSubmit={handleUpdateSettings} className="overflow-y-auto flex-1 p-6">
              <div className="space-y-6">
                {/* Play Style */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Play Style
                  </label>
                  <select
                    value={settingsForm.playStyle}
                    onChange={(e) => setSettingsForm({ ...settingsForm, playStyle: e.target.value as any })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="AGGRESSIVE">Aggressive</option>
                    <option value="PASSIVE">Passive</option>
                    <option value="BALANCED">Balanced</option>
                  </select>
                </div>

                {/* Betting Patterns */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Aggressiveness (Betting Pattern): {Math.round((settingsForm.aggressiveness || 0) * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={settingsForm.aggressiveness || 0}
                    onChange={(e) => setSettingsForm({ ...settingsForm, aggressiveness: parseFloat(e.target.value) })}
                    className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-500"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Conservative</span>
                    <span>Aggressive</span>
                  </div>
                </div>

                {/* Fold Probability */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fold Probability: {Math.round((settingsForm.foldProbability || 0) * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={settingsForm.foldProbability || 0}
                    onChange={(e) => setSettingsForm({ ...settingsForm, foldProbability: parseFloat(e.target.value) })}
                    className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-500"
                  />
                </div>

                {/* Raise Frequency */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Raise Frequency: {Math.round((settingsForm.raiseProbability || 0) * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={settingsForm.raiseProbability || 0}
                    onChange={(e) => setSettingsForm({ ...settingsForm, raiseProbability: parseFloat(e.target.value) })}
                    className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500"
                  />
                </div>

                {/* Call Frequency */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Call Frequency: {Math.round((settingsForm.callProbability || 0) * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={settingsForm.callProbability || 0}
                    onChange={(e) => setSettingsForm({ ...settingsForm, callProbability: parseFloat(e.target.value) })}
                    className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                  />
                </div>

                {/* Bluff Settings */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bluff Frequency: {Math.round((settingsForm.bluffFrequency || 0) * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={settingsForm.bluffFrequency || 0}
                    onChange={(e) => setSettingsForm({ ...settingsForm, bluffFrequency: parseFloat(e.target.value) })}
                    className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-500"
                  />
                </div>

                {/* Tightness */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tightness (Hand Selection): {Math.round((settingsForm.tightness || 0) * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={settingsForm.tightness || 0}
                    onChange={(e) => setSettingsForm({ ...settingsForm, tightness: parseFloat(e.target.value) })}
                    className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-500"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Loose (Many Hands)</span>
                    <span>Tight (Few Hands)</span>
                  </div>
                </div>

                {/* Reaction Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reaction Time Delay: {settingsForm.reactionTimeMs || 0}ms
                  </label>
                  <input
                    type="range"
                    min="500"
                    max="5000"
                    step="100"
                    value={settingsForm.reactionTimeMs || 2000}
                    onChange={(e) => setSettingsForm({ ...settingsForm, reactionTimeMs: parseInt(e.target.value) })}
                    className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-500"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Fast (500ms)</span>
                    <span>Slow (5000ms)</span>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => { setShowSettingsModal(false); setCurrentBot(null); }}
                  className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                >
                  Save Settings
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Statistics Modal */}
      {showStatsModal && currentBot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4 text-white">
              <h2 className="text-2xl font-bold">Bot Statistics</h2>
              <p className="text-purple-100 text-sm mt-1">{currentBot.avatar} {currentBot.name}</p>
            </div>

            <div className="overflow-y-auto flex-1 p-6">
              {loadingStats ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                  <div className="text-gray-500 mt-4">Loading statistics...</div>
                </div>
              ) : botStats ? (
                <div className="space-y-6">
                  {/* Overview Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <div className="text-sm text-blue-600 font-semibold">Total Games</div>
                      <div className="text-3xl font-bold text-blue-900 mt-1">{botStats.totalGames}</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                      <div className="text-sm text-green-600 font-semibold">Win Rate</div>
                      <div className="text-3xl font-bold text-green-900 mt-1">{botStats.winRate.toFixed(1)}%</div>
                      <div className="text-xs text-green-600 mt-1">{botStats.wins}W / {botStats.losses}L</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                      <div className="text-sm text-purple-600 font-semibold">Total Chips Won</div>
                      <div className="text-2xl font-bold text-purple-900 mt-1">
                        {botStats.totalChipsWon.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {/* Detailed Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="text-sm font-semibold text-gray-700 mb-2">Average Chips Won</div>
                      <div className="text-2xl font-bold text-gray-900">
                        {Math.round(botStats.avgChipsWon).toLocaleString()}
                      </div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="text-sm font-semibold text-gray-700 mb-2">Avg Game Duration</div>
                      <div className="text-2xl font-bold text-gray-900">
                        {Math.floor(botStats.avgGameDuration / 60)}m {botStats.avgGameDuration % 60}s
                      </div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="text-sm font-semibold text-gray-700 mb-2">Biggest Win</div>
                      <div className="text-2xl font-bold text-green-600">
                        +{botStats.biggestWin.toLocaleString()}
                      </div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="text-sm font-semibold text-gray-700 mb-2">Biggest Loss</div>
                      <div className="text-2xl font-bold text-red-600">
                        -{botStats.biggestLoss.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {/* Favorite Hands */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-800 mb-3">Favorite Starting Hands</h3>
                    <div className="flex flex-wrap gap-3">
                      {botStats.favoriteHands.map((hand, idx) => (
                        <div key={idx} className="bg-white border-2 border-gray-300 rounded-lg px-4 py-2 font-bold text-lg">
                          {hand}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Most Common Moves */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-800 mb-3">Most Common Moves</h3>
                    <div className="space-y-3">
                      {botStats.mostCommonMoves.map((move, idx) => (
                        <div key={idx}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="font-medium text-gray-700">{move.move}</span>
                            <span className="text-gray-600">{move.count} times</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                              className={`h-3 rounded-full ${
                                move.move === 'FOLD' ? 'bg-red-500' :
                                move.move === 'CALL' ? 'bg-blue-500' :
                                move.move === 'RAISE' ? 'bg-green-500' :
                                'bg-purple-500'
                              }`}
                              style={{ width: `${(move.count / botStats.totalGames) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => { setShowStatsModal(false); setCurrentBot(null); setBotStats(null); }}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deploy Modal */}
      {showDeployModal && deployingBotId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4 text-white">
              <h2 className="text-2xl font-bold">Deploy Bot to Table</h2>
            </div>

            <form onSubmit={handleDeploy} className="p-6">
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Table
                </label>
                <select
                  required
                  value={deployTableId}
                  onChange={(e) => setDeployTableId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">-- Choose a table --</option>
                  {tables.map(table => (
                    <option key={table.id} value={table.id}>
                      {table.name} ({table.currentPlayers}/{table.maxPlayers} players)
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => { setShowDeployModal(false); setDeployingBotId(null); setDeployTableId(''); }}
                  className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
                >
                  Deploy Bot
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BotsPage;
